import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import DialogTitle from '@mui/joy/DialogTitle'
import Typography from '@mui/joy/Typography'
import { IFeature, isPerTabCompanySwitchEnabled } from '@numeo/types-core'
import axios from 'axios'
import { clearSignupGates, setAuthToken, stripEntityIdFromPath, switchAuthToken } from 'setup/SetupAxios'
import { useState } from 'react'
import { CompanyInfoStep } from 'pages/auth/pages/onboarding/components/CompanyInfoStep'
import { CompanyInfoFormData } from 'pages/auth/pages/onboarding/types'

interface AddCompanyModalProps {
    open: boolean
    onClose: () => void
}

export const AddCompanyModal = ({ open, onClose }: AddCompanyModalProps) => {
    const [error, setError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const handleCompanySubmit = async (data: CompanyInfoFormData) => {
        if (submitting) return
        setError('')
        setSubmitting(true)

        try {
            const res = await axios.post<{ success: boolean; data: { application: { id: string; features?: IFeature[] } } }>('/organizations/companies', {
                companyName: data.companyName,
                mcNumber: data.mcNumber,
                dotNumber: data.dotNumber || undefined,
                dba: data.dba || undefined,
                fein: data.fein || undefined,
                address1: data.address1 || undefined,
                address2: data.address2 || undefined,
                city: data.city || undefined,
                state: data.state || undefined,
                zipCode: data.zipCode || undefined,
                email: data.email || undefined,
                phoneNumber: data.phoneNumber || undefined,
                details: data.details || undefined,
            })

            if (!res.data.success) {
                setError('Failed to add company')
                setSubmitting(false)
                return
            }

            const switchRes = await axios.post<{ success: boolean; data: { token: string } }>('/organizations/switch', {
                applicationId: res.data.data.application.id,
            })

            // Server returned but without a token — treat as a hard failure, never mutate local state
            if (!switchRes.data.success || !switchRes.data.data?.token) {
                setError('Company added, but failed to switch to it. Please refresh and try again.')
                setSubmitting(false)
                return
            }

            // A new company INHERITS feature flags from the org's anchor app (see addCompany's
            // inheritedFeatures — Updater, AIEmail, Fuel AI, and this one all carry over so a new
            // company doesn't silently start with everything off), so it can already have
            // FeatureType.PerTabCompanySwitch enabled at creation time. Must check it here, same as
            // every other switch/accept entry point — assuming "always off for a brand-new company"
            // would wrongly force the old global-switch path for orgs that already opted in.
            const usePerTabSwitch = isPerTabCompanySwitchEnabled(res.data.data.application)
            const persistToken = usePerTabSwitch ? switchAuthToken : setAuthToken
            persistToken(switchRes.data.data.token)
            clearSignupGates()
            window.location.replace(stripEntityIdFromPath())
        } catch (err) {
            if (axios.isAxiosError(err)) {
                setError(err.response?.data?.message || 'Failed to add company')
            } else {
                setError('Failed to add company')
            }
            setSubmitting(false)
        }
    }

    const handleClose = () => {
        setError('')
        onClose()
    }

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog sx={{ width: 640, maxWidth: '95vw', borderRadius: 'lg', p: 3 }}>
                <ModalClose />
                <DialogTitle>Add New Company</DialogTitle>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1 }}>
                    Enter the company details for your new Motor Carrier.
                </Typography>
                {error && (
                    <Typography color="danger" level="body-sm" sx={{ mb: 1 }}>
                        {error}
                    </Typography>
                )}
                <CompanyInfoStep onNext={handleCompanySubmit} initialData={{}} submitLabel="Add Company" />
            </ModalDialog>
        </Modal>
    )
}
